import React, { useEffect, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ChartData,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { useIsMobile } from '@/hooks';
import { useTranslation } from 'react-i18next';

type GraficaBoxes = {
  mes: string;
  valorMCN: number;
  valorMCNSpecified: boolean;
  valorTCP: number;
  valorTCPSpecified: boolean;
};

type OneLabelToBarsWithXChartProps = {
  data: GraficaBoxes[];
};

const monthMapES: { [key: string]: string } = {
  Ene: 'Ene',
  Feb: 'Feb',
  Mar: 'Mar',
  Abr: 'Abr',
  May: 'May',
  Jun: 'Jun',
  Jul: 'Jul',
  Ago: 'Ago',
  Sep: 'Sep',
  Oct: 'Oct',
  Nov: 'Nov',
  Dic: 'Dic',
};

const monthMapEN: { [key: string]: string } = {
  Ene: 'Jan',
  Feb: 'Feb',
  Mar: 'Mar',
  Abr: 'Apr',
  May: 'May',
  Jun: 'Jun',
  Jul: 'Jul',
  Ago: 'Aug',
  Sep: 'Sep',
  Oct: 'Oct',
  Nov: 'Nov',
  Dic: 'Dec',
};
export const getOneLabelTwoDataChartXAxisData = (
  items: GraficaBoxes[],
  language: string
): ChartData<'bar'> => {
  const labels = items.map((item) => {
    const [abrevMes, year] = item.mes.split('-');
    const monthName = language.includes('es')
      ? monthMapES[abrevMes]
      : monthMapEN[abrevMes];
    return `${monthName} ${year}`;
  });

  const valorMCNData = items.map((item) =>
    parseFloat(item.valorMCN.toFixed(1))
  );
  const valorTCPData = items.map((item) =>
    parseFloat(item.valorTCP.toFixed(1))
  );

  return {
    labels,
    datasets: [
      {
        label: 'Valor MCN',
        data: valorMCNData,
        backgroundColor: '#4780bf',
        borderRadius: 6,
        barThickness: 30,
      },
      {
        label: 'Valor TCP',
        data: valorTCPData,
        backgroundColor: '#D3D3D3',
        borderRadius: 6,
        barThickness: 30,
      },
    ],
  };
};

/* eslint-disable*/
const valueAboveBarsPlugin = {
  id: 'valueAboveBars',
  afterDatasetsDraw: (chart: any) => {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset: any, i: any) => {
      const meta = chart.getDatasetMeta(i);
      meta.data.forEach((bar: any, index: any) => {
        const value = dataset.data[index];
        ctx.fillStyle = '#223466';
        ctx.font = 'bold 14px';
        ctx.textAlign = 'center';
        const position = bar.tooltipPosition();
        ctx.fillText(value + '%', position.x, position.y - 10);
      });
    });
  },
};

const OneLabelToBarsWithXChart: React.FC<OneLabelToBarsWithXChartProps> = ({
  data,
}) => {
  const { i18n } = useTranslation();

  const chartRef = useRef<ChartJS<'bar'> | null>(null);
  const chartData: ChartData<'bar'> = getOneLabelTwoDataChartXAxisData(
    data,
    i18n.language
  );
  const isMobile = useIsMobile();
  ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);
  ChartJS.unregister(valueAboveBarsPlugin);

  const options = {
    layout: {
      maintainAspectRatio: false,
      padding: {
        top: isMobile ? 0 : 20, // Añadimos un padding superior para que los valores no se corten
      },
    },
    scales: {
      y: {
        display: true, // Mostrar eje Y
        lineWidth: 0,
      },
      x: {
        display: true,
        ticks: {
          autoSkip: false,
          maxRotation: 90,
          minRotation: 0,
        },
      },
    },
    plugins: {
      legend: {
        display: false, // No mostrar leyenda
      },
    },
    barPercentage: 0.7,
  };

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, []);

  return (
    <Bar
      style={{ height: isMobile ? '220px' : '250px' }}
      data={chartData}
      options={options}
    />
  );
};

export default OneLabelToBarsWithXChart;
